export interface BaseJSON {
  id: string;
}

export abstract class BaseModel {
  id: string;
  origin: string;

  /**
   * Constructor of the Basemodel class
   * @param id database id of the object
     @param origin origin of the API calls
   */
   /*note: since the same backend API is called also in event dashboard
   when updating a registration, we need to specify the origin where the request comes from
   */

  protected constructor(id: string, origin='participant_dashboard') {
    this.id = id;
    this.origin = origin;
  }
}

/**************************************/
/**************************************/
/**************************************/

export interface BusinessPartnerMetaJSON extends BaseJSON {
  name: string;
  booked_digital_branding: boolean;
  logo: string;
  homepage_url: string;
}

export class BusinessPartnerMeta extends BaseModel {
  name: string;
  bookedDigitalBranding: boolean;
  logo: string;
  homepageUrl: string;

  constructor(
    id: string,
    name: string,
    bookedDigitalBranding: boolean,
    logo: string,
    homepageUrl: string
  ) {
    super(id);

    this.name = name;
    this.bookedDigitalBranding = bookedDigitalBranding;
    this.logo = logo;
    this.homepageUrl = homepageUrl;
  }
}

export class BusinessPartnerMetaFactory {

  fromJSON(json: BusinessPartnerMetaJSON): BusinessPartner {
    return new BusinessPartnerMeta(
      json.id,
      json.name,
      json.booked_digital_branding,
      json.logo,
      json.homepage_url
    );
  }

  toJSON(businessPartner: BusinessPartnerMeta): BusinessPartnerMetaJSON {
    return {
      id: businessPartner.id,
      booked_digital_branding: businessPartner.bookedDigitalBranding,
      name: businessPartner.name,
      logo: businessPartner.logo,
      homepage_url: businessPartner.homepageUrl
    };
  }
}

/**************************************/
/**************************************/
/**************************************/

export interface RegistrationJSON extends BaseJSON {
  name: string;
  title: string;
  type: string;
  company: string;
  contact: string;
  business_partner: string;
  participation_status: string;
  portal_access: string;
  event_option: string;
  event_group: number;
  linkedin_url: string;
  think_round1: boolean;
  think_round2: boolean;
  dinner_round1: boolean;
  dinner_round2: boolean;
  booked_branding: boolean;
  booked_dinner_hosting: boolean;
  booked_think_tank_suggestion: boolean;
  booked_think_tank_selection: boolean;
  booked_think_tank_id: string;
  priorities: string[];
  optimal_seatings: string[];
  optimal_dinner_seatings: number[];
  origin: string;
}

export class Registration extends BaseModel {
  name: string;
  title: string;
  type: string;
  company: string;
  contact: string;
  businessPartner: string;
  participationStatus: string;
  portalAccess: string;
  eventOption: string;
  eventGroup: number;
  linkedinUrl: string;
  thinkRound1: boolean;
  thinkRound2: boolean;
  dinnerRound1: boolean;
  dinnerRound2: boolean;
  bookedBranding: boolean;
  bookedDinnerHosting: boolean;
  bookedThinkTankSuggestion: boolean;
  bookedThinkTankSelection: boolean;
  bookedThinkTankId: string;
  priorities: string[];
  optimalSeatings: string[];
  optimalDinnerSeatings: number[];

  constructor(
    id: string,
    name: string,
    title: string,
    type: string,
    company: string,
    contact: string,
    businessPartner: string,
    participationStatus: string,
    portalAccess: string,
    eventOption: string,
    eventGroup: number,
    linkedinUrl: string,
    thinkRound1: boolean,
    thinkRound2: boolean,
    dinnerRound1: boolean,
    dinnerRound2: boolean,
    bookedBranding: boolean,
    bookedDinnerHosting: boolean,
    bookedThinkTankSuggestion: boolean,
    bookedThinkTankSelection: boolean,
    bookedThinkTankId: string,
    priorities: string[],
    optimalSeatings: string[],
    optimalDinnerSeatings: number[]
  ) {
    super(id);

    this.name = name;
    this.title = title;
    this.type = type;
    this.company = company;
    this.contact = contact;
    this.businessPartner = businessPartner;
    this.participationStatus = participationStatus;
    this.portalAccess = portalAccess;
    this.eventOption = eventOption;
    this.eventGroup = eventGroup;
    this.linkedinUrl = linkedinUrl;
    this.thinkRound1 = thinkRound1;
    this.thinkRound2 = thinkRound2;
    this.dinnerRound1 = dinnerRound1;
    this.dinnerRound2 = dinnerRound2;
    this.bookedBranding = bookedBranding;
    this.bookedDinnerHosting = bookedDinnerHosting;
    this.bookedThinkTankSuggestion = bookedThinkTankSuggestion;
    this.bookedThinkTankSelection = bookedThinkTankSelection;
    this.bookedThinkTankId = bookedThinkTankId;
    this.priorities = priorities;
    this.optimalSeatings = optimalSeatings;
    this.optimalDinnerSeatings = optimalDinnerSeatings;
  }
}

export class RegistrationFactory {
  /**
   *
   * @param json
   */
  fromJSON(json: RegistrationJSON): Registration {
    return new Registration(
      json.id,
      json.name,
      json.title,
      json.type,
      json.company,
      json.contact,
      json.business_partner,
      json.participation_status,
      json.portal_access,
      json.event_option,
      json.event_group,
      json.linkedin_url,
      json.think_round1,
      json.think_round2,
      json.dinner_round1,
      json.dinner_round2,
      json.booked_branding,
      json.booked_dinner_hosting,
      json.booked_think_tank_suggestion,
      json.booked_think_tank_selection,
      json.booked_think_tank_id,
      json.priorities,
      json.optimal_seatings,
      json.optimal_dinner_seatings
    );
  }

  init(): Registration {
    return new Registration(
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      0,
      "",
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      "",
      [],
      [],
      [],
    )


  }

  /**
   *
   * @param contact
   */
  toJSON(registration: Registration): RegistrationJSON {
    return {
      id: registration.id,
      name: registration.name,
      title: registration.title,
      type: registration.type,
      company: registration.company,
      contact: registration.contact,
      business_partner: registration.businessPartner,
      participation_status: registration.participationStatus,
      portal_access: registration.portalAccess,
      event_option: registration.eventOption,
      event_group: registration.eventGroup,
      linkedin_url: registration.linkedinUrl,
      think_round1: registration.thinkRound1,
      think_round2: registration.thinkRound2,
      dinner_round1: registration.dinnerRound1,
      dinner_round2: registration.dinnerRound2,
      booked_branding: registration.bookedBranding,
      booked_dinner_hosting: registration.bookedDinnerHosting,
      booked_think_tank_suggestion: registration.bookedThinkTankSuggestion,
      booked_think_tank_selection: registration.bookedThinkTankSelection,
      booked_think_tank_id: registration.bookedThinkTankId,
      priorities: registration.priorities,
      optimal_seatings: registration.optimalSeatings,
      optimal_dinner_seatings: registration.optimalDinnerSeatings,
      origin: registration.origin,
    };
  }
}

export interface ProfileJSON extends BaseJSON {
  registration_id: string;
  registration_type: string;
  description: string;
  dietary_needs: string;
  email: string;
  address: string;
  linkedin_url: string;
  phone_number: string;
  mobile_phone_number: string;
  allow_email_sharing: boolean;
  allow_phone_sharing: boolean;
  allow_dietary_needs_sharing: boolean;
  accept_legal_notice: boolean;
  feedback_networking_opportunities: number;
  feedback_executive_interactions: number;
  feedback_topics_contents: number;
  feedback_location_food: number;
  feedback_overall_experience: number
  feedback_interested_in_upcoming_event: string;
  feedback_additional: string;
}

export class Profile extends BaseModel {
  registrationID: string;
  registrationType: string;
  description: string;
  dietaryNeeds: string;
  email: string;
  address: string;
  linkedinUrl: string;
  phoneNumber: string;
  mobilePhoneNumber: string;
  allowEmailSharing: boolean;
  allowPhoneSharing: boolean;
  allowDietaryNeedsSharing: boolean;
  acceptLegalNotice: boolean;
  feedbackNetworkingOpportunities: number;
  feedbackExecutiveInteractions: number;
  feedbackTopicsContents: number;
  feedbackLocationFood: number;
  feedbackOverallExperience: number
  feedbackInterestedInUpcomingEvent: string;
  feedbackAdditional: string;

  constructor(
    id: string,
    registrationID: string,
    registrationType: string,
    description: string,
    dietaryNeeds: string,
    email: string,
    address: string,
    linkedinUrl: string,
    phoneNumber: string,
    mobilePhoneNumber: string,
    allowEmailSharing: boolean,
    allowPhoneSharing: boolean,
    allowDietaryNeedsSharing: boolean,
    acceptLegalNotice: boolean,
    feedbackNetworkingOpportunities: number,
    feedbackExecutiveInteractions: number,
    feedbackTopicsContents: number,
    feedbackLocationFood: number,
    feedbackOverallExperience: number,
    feedbackInterestedInUpcomingEvent: string,
    feedbackAdditional: string
  ) {
    super(id);

    this.registrationID = registrationID;
    this.registrationType = registrationType;
    this.description = description;
    this.dietaryNeeds = dietaryNeeds;
    this.email = email;
    this.address = address;
    this.linkedinUrl = linkedinUrl;
    this.phoneNumber = phoneNumber;
    this.mobilePhoneNumber = mobilePhoneNumber;
    this.allowEmailSharing = allowEmailSharing;
    this.allowPhoneSharing = allowPhoneSharing;
    this.allowDietaryNeedsSharing = allowDietaryNeedsSharing;
    this.acceptLegalNotice = acceptLegalNotice;
    this.feedbackNetworkingOpportunities = feedbackNetworkingOpportunities;
    this.feedbackExecutiveInteractions = feedbackExecutiveInteractions;
    this.feedbackTopicsContents = feedbackTopicsContents;
    this.feedbackLocationFood = feedbackLocationFood;
    this.feedbackOverallExperience = feedbackOverallExperience;
    this.feedbackInterestedInUpcomingEvent = feedbackInterestedInUpcomingEvent;
    this.feedbackAdditional = feedbackAdditional;
  }
}

export class ProfileFactory {
  fromJSON(json: ProfileJSON): Profile {
    return new Profile(
      json.id,
      json.registration_id,
      json.registration_type,
      json.description,
      json.dietary_needs,
      json.email,
      json.address,
      json.linkedin_url,
      json.phone_number,
      json.mobile_phone_number,
      json.allow_email_sharing,
      json.allow_phone_sharing,
      json.allow_dietary_needs_sharing,
      json.accept_legal_notice,
      json.feedback_networking_opportunities,
      json.feedback_executive_interactions,
      json.feedback_topics_contents,
      json.feedback_location_food,
      json.feedback_overall_experience,
      json.feedback_interested_in_upcoming_event,
      json.feedback_additional
    );
  }

  toJSON(profile: Profile): ProfileJSON {
    return {
      id: profile.id,
      registration_id: profile.registrationID,
      registration_type: profile.registrationType,
      description: profile.description,
      dietary_needs: profile.dietaryNeeds,
      email: profile.email,
      address: profile.address,
      linkedin_url: profile.linkedinUrl,
      phone_number: profile.phoneNumber,
      mobile_phone_number: profile.mobilePhoneNumber,
      allow_email_sharing: profile.allowEmailSharing,
      allow_phone_sharing: profile.allowPhoneSharing,
      allow_dietary_needs_sharing: profile.allowDietaryNeedsSharing,
      accept_legal_notice: profile.acceptLegalNotice,
      feedback_networking_opportunities: profile.feedbackNetworkingOpportunities,
      feedback_executive_interactions: profile.feedbackExecutiveInteractions,
      feedback_topics_contents: profile.feedbackTopicsContents,
      feedback_location_food: profile.feedbackLocationFood,
      feedback_overall_experience: profile.feedbackOverallExperience,
      feedback_interested_in_upcoming_event: profile.feedbackInterestedInUpcomingEvent,
      feedback_additional: profile.feedbackAdditional
    };
  }

  init(): Profile {
    return new Profile(
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      false,
      false,
      false,
      false,
      null,
      null,
      null,
      null,
      null,
      null,
      null
    )
  }


}

export interface EventJSON extends BaseJSON {
  name: string;
  description: string;
  venue: string;
  venue_name: string;
  venue_city: string;
  venue_country: string;
  venue_image: string;
  venue_postal_code: string;
  venue_status: string;
  venue_street_address_1: string;
  venue_street_address_2: string;
  room_name: string;
  event_date: string;
}

export class Event extends BaseModel {
  name: string;
  description: string;
  venue: string;
  venueName: string;
  venueCity: string;
  venueCountry: string;
  venueImage: string;
  venuePostalCode: string;
  venueStatus: string;
  venueStreetAddress1: string;
  venueStreetAddress2: string;
  roomName: string;
  eventDate: string;

  constructor(
    id: string,
    name: string,
    description: string,
    venue: string,
    venueName: string,
    venueCity: string,
    venueCountry: string,
    venueImage: string,
    venuePostalCode: string,
    venueStatus: string,
    venueStreetAddress1: string,
    venueStreetAddress2: string,
    roomName: string,
    eventDate: string
  ) {
    super(id);

    this.name = name;
    this.description = description;
    this.venue = venue;
    this.venueName = venueName;
    this.venueCity = venueCity;
    this.venueCountry = venueCountry;
    this.venueImage = venueImage;
    this.venuePostalCode = venuePostalCode;
    this.venueStatus = venueStatus;
    this.venueStreetAddress1 = venueStreetAddress1;
    this.venueStreetAddress2 = venueStreetAddress2;
    this.roomName = roomName;
    this.eventDate = eventDate;
  }
}

export class EventFactory {
  /**
   *
   * @param json
   */
  fromJSON(json: EventJSON): Event {
    return new Event(
      json.id,
      json.name,
      json.description,
      json.venue,
      json.venue_name,
      json.venue_city,
      json.venue_country,
      json.venue_image,
      json.venue_postal_code,
      json.venue_status,
      json.venue_street_address_1,
      json.venue_street_address_2,
      json.room_name,
      json.event_date
    );
  }

  /**
   *
   * @param contact
   */
  toJSON(event: Event): EventJSON {
    return {
      id: event.id,
      name: event.name,
      description: event.description,
      venue: event.venue,
      venue_name: event.venueName,
      venue_city: event.venueCity,
      venue_country: event.venueCountry,
      venue_image: event.venueImage,
      venue_postal_code: event.venuePostalCode,
      venue_status: event.venueStatus,
      venue_street_address_1: event.venueStreetAddress1,
      venue_street_address_2: event.venueStreetAddress2,
      room_name: event.roomName,
      event_date: event.eventDate,
    };
  }


  init(): Event {
    return new Event(
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
    )
  }

}

export interface ThinkTankKeywordJSON extends BaseJSON {
  title: string;
  submitted_by_registration_id: string;
  active: boolean;
  read_only: boolean;
  status: number;
}

export class ThinkTankKeyword extends BaseModel {
  title: string;
  submittedByRegistrationId: string;
  active: boolean;
  readOnly: boolean;
  status: number;

  constructor(
      id: string,
      title: string,
      submittedByRegistrationId: string,
      active: boolean,
      readOnly: boolean,
      status: number,
  ) {
    super(id);
    this.title = title;
    this.submittedByRegistrationId = submittedByRegistrationId;
    this.active = active;
    this.readOnly = readOnly;
    this.status = status;
  }
}

export class ThinkTankKeywordFactory {
  /**
   *
   * @param json
   */
  fromJSON(json: ThinkTankKeywordJSON): ThinkTankKeyword {
    return new ThinkTankKeyword(
      json.id,
      json.title,
      json.submitted_by_registration_id,
      json.active,
      json.read_only,
      json.status
    )
  }

  toJSON(thinkTankKeyword: ThinkTankKeyword): ThinkTankKeywordJSON {
    return {
      id: thinkTankKeyword.id,
      title: thinkTankKeyword.title,
      submitted_by_registration_id: thinkTankKeyword.submittedByRegistrationId,
      active: thinkTankKeyword.active,
      read_only: thinkTankKeyword.readOnly,
      status: thinkTankKeyword.status
    };
  }
}


export interface ThinkTankContentJSON extends BaseJSON {
  title: string;
  content_en: string;
  submitted_by_registration_id: string;
  active: boolean;
  read_only: boolean;
  status: number;
  weight: number;
  keywords: ThinkTankKeywordJSON [];
}

export class ThinkTankContent extends BaseModel {
  title: string;
  contentEn: string;
  submittedByRegistrationId: string;
  active: boolean;
  readOnly: boolean;
  status: number;
  weight: number;
  keywords: ThinkTankKeyword[];

  constructor(
      id: string,
      title: string,
      contentEn: string,
      submittedByRegistrationId: string,
      active: boolean,
      readOnly: boolean,
      status: number,
      weight: number,
      keywords: ThinkTankKeyword[],
  ) {
    super(id);

    this.title = title;
    this.contentEn = contentEn;
    this.submittedByRegistrationId = submittedByRegistrationId;
    this.active = active;
    this.readOnly = readOnly;
    this.status = status;
    this.weight = weight;
    this.keywords = keywords;
  }
}

export class ThinkTankContentFactory {
  /**
   *
   * @param json
   */
  fromJSON(json: ThinkTankContentJSON): ThinkTankContent {
    return new ThinkTankContent(
      json.id,
      json.title,
      json.content_en,
      json.submitted_by_registration_id,
      json.active,
      json.read_only,
      json.status,
      json.weight,
      json.keywords.map((thisKeyword: ThinkTankKeywordJSON) => new ThinkTankKeywordFactory().fromJSON(thisKeyword))
    )
  }

  toJSON(thinkTankContent: ThinkTankContent): ThinkTankContentJSON {
    return {
      id: thinkTankContent.id,
      title: thinkTankContent.title,
      content_en: thinkTankContent.contentEn,
      submitted_by_registration_id: thinkTankContent.submittedByRegistrationId,
      active: thinkTankContent.active,
      read_only: thinkTankContent.readOnly,
      status: thinkTankContent.status,
      weight: thinkTankContent.weight,
      keywords: thinkTankContent.keywords.map((thisKeyword: ThinkTankKeyword) => new ThinkTankKeywordFactory().toJSON(thisKeyword))
    };
  }
}


export interface ThinkTankDynamicContentJSON extends BaseJSON {
  event_id: string;
  registration_id: string;
  real_table_id: string;
  topic: string;
  weight: number;
}

export class ThinkTankDynamicContent extends BaseModel {
  eventId: string;
  registrationId: string;
  realTableId: string;
  topic: string;
  weight:number;

  constructor(
    id: string,
    eventId: string,
    registrationId: string,
    realTableId: string,
    topic: string,
    weight: number
  ) {
    super(id);

    this.eventId = eventId;
    this.registrationId = registrationId;
    this.realTableId = realTableId;
    this.topic = topic;
    this.weight = weight;
  }
}

export class ThinkTankDynamicContentFactory {
  fromJSON(json: ThinkTankDynamicContentJSON): ThinkTankDynamicContent {
    return new ThinkTankDynamicContent(
      json.id,
      json.event_id,
      json.registration_id,
      json.real_table_id,
      json.topic,
      json.weight
    );
  }

  toJSON(thinkTankDynamicContent: ThinkTankDynamicContent): ThinkTankDynamicContentJSON {
    return {
      id: thinkTankDynamicContent.id,
      event_id: thinkTankDynamicContent.eventId,
      registration_id: thinkTankDynamicContent.registrationId,
      real_table_id: thinkTankDynamicContent.realTableId,
      topic: thinkTankDynamicContent.topic,
      weight: thinkTankDynamicContent.weight
    };
  }

  init(eventId: string,
       registrationId: string,
       realTableId: string,
  ): ThinkTankDynamicContent {
    return new ThinkTankDynamicContent(
      "",
      eventId,
      registrationId,
      realTableId,
      "",
      0
    )
  }
}


export interface ThinkTankPriorityJSON extends BaseJSON {
  registration_id: string;
  parent_think_tank_db_id: string;
  think_tank_db_id: string;
  priority: number;
}

export class ThinkTankPriority extends BaseModel {
  registrationId: string;
  parentThinkTankDbId: string;
  thinkTankDbId: string;
  priority: number;

  constructor(
      id: string,
      registrationId: string,
      parentThinkTankDbId: string,
      thinkTankDbId: string,
      priority: number
  ) {
    super(id);

    this.registrationId = registrationId;
    this.parentThinkTankDbId = parentThinkTankDbId;
    this.thinkTankDbId = thinkTankDbId;
    this.priority = priority;
  }
}

export class ThinkTankPriorityFactory {
  /**
   *
   * @param json
   */
  fromJSON(json: ThinkTankPriorityJSON): ThinkTankPriority {
    return new ThinkTankPriority(
      json.id,
      json.registration_id,
      json.parent_think_tank_db_id,
      json.think_tank_db_id,
      json.priority
    );
  }

  toJSON(thinkTankPriority: ThinkTankPriority): ThinkTankPriorityJSON {
    return {
      id: thinkTankPriority.id,
      registration_id: thinkTankPriority.registrationId,
      parent_think_tank_db_id: thinkTankPriority.parentThinkTankDbId,
      think_tank_db_id: thinkTankPriority.thinkTankDbId,
      priority: thinkTankPriority.priority
  }
}
}

export interface ThinkTankTableJSON extends BaseJSON {
  name: string;
  title: string;
  contents: ThinkTankContentJSON [];
}

export class ThinkTankTable extends BaseModel {
  name: string;
  title: string;
  contents: ThinkTankContent [];

  constructor(
      id: string,
      name: string,
      title: string,
      contents: ThinkTankContent []
  ) {
    super(id);

    this.name = name;
    this.title = title;
    this.contents = contents;
  }
}

export class ThinkTankTableFactory {
  /**
   *
   * @param json
   */
  fromJSON(json: ThinkTankTableJSON): ThinkTankTable {
    if(json == null){
      return null
    }
    return new ThinkTankTable(
      json.id,
      json.name,
      json.title,
      json.contents.map((thinkTankContentJSON: ThinkTankContentJSON) => new ThinkTankContentFactory().fromJSON(thinkTankContentJSON))
      )
  }

  toJSON(thinkTankTable: ThinkTankTable): ThinkTankTableJSON {
    if(thinkTankTable == null){
      return null
    }

    return {
      id: thinkTankTable.id,
      title: thinkTankTable.title,
      name: thinkTankTable.name,
      contents: thinkTankTable.contents.map((thinkTankContent: ThinkTankContent) => new ThinkTankContentFactory().toJSON(thinkTankContent))
    };
  }
}

export interface ThinkTankRealTableJSON extends BaseJSON {
  id: string;
  table_number: number;
  round_number: number;
  think_tank_table: ThinkTankTable;
  voting_time_end: string;
  dynamic_contents: ThinkTankDynamicContentJSON [];

}

export class ThinkTankRealTable extends BaseModel {
  tableNumber: number;
  roundNumber: number;
  thinkTankTable: ThinkTankTable;
  votingTimeEnd: string;
  dynamicContents: ThinkTankDynamicContent [];

  constructor(
    id: string,
    tableNumber: number,
    roundNumber: number,
    thinkTankTable: ThinkTankTable,
    votingTimeEnd: string,
    dynamicContents: ThinkTankDynamicContent [],
  ) {
    super(id);

    this.tableNumber = tableNumber;
    this.roundNumber = roundNumber;
    this.votingTimeEnd = votingTimeEnd;
    this.thinkTankTable = thinkTankTable;
    this.dynamicContents = dynamicContents;
  }
}

export class ThinkTankRealTableFactory {
  fromJSON(json: ThinkTankRealTableJSON): ThinkTankRealTable {
    return new ThinkTankRealTable(
      json.id,
      json.table_number,
      json.round_number,
      new ThinkTankTableFactory().fromJSON(json.think_tank_table),
      json.voting_time_end,
      json.dynamic_contents.map((thinkTankDynamicContentJSON: ThinkTankDynamicContentJSON) => new ThinkTankDynamicContentFactory().fromJSON(thinkTankDynamicContentJSON))
    );
  }

  toJSON(thinkTankRealTable: ThinkTankRealTable): ThinkTankRealTableJSON {
    return {
      id: thinkTankRealTable.id,
      table_number: thinkTankRealTable.tableNumber,
      round_number: thinkTankRealTable.roundNumber,
      think_tank_table: new ThinkTankTableFactory().toJSON(thinkTankRealTable.thinkTankTable),
      voting_time_end: thinkTankRealTable.votingTimeEnd,
      dynamic_contents: thinkTankRealTable.dynamicContents.map((thinkTankDynamicContent: ThinkTankDynamicContent) => new ThinkTankDynamicContentFactory().toJSON(thinkTankDynamicContent))
    };
  }
}

export enum AgendaElementType {
  ThinkTankRound = 0,
  DinnerRound,
  KeynoteRound,
  Other,
}

export class AgendaElement extends BaseModel {
  type: AgendaElementType;
  name: string;
  description: string;
  startTime: string;
  endTime: string;
  detailComponent?: any;

  constructor(
    id: string,
    type: AgendaElementType,
    name: string,
    description: string,
    startTime: string,
    endTime: string,
    detailComponent?: any
  ) {
    super(id);

    this.type = type;
    this.name = name;
    this.description = description;
    this.startTime = startTime;
    this.endTime = endTime;
    this.detailComponent = detailComponent;
  }
}


export interface ThinkTankSeatingJSON extends BaseJSON {
  id: string;
  event_id: string;
  think_tank_real_table: ThinkTankRealTableJSON;
  weight: string;
  round: number;
  dynamic_content_voting: [string];
  profile: ProfileJSON;
}

export class ThinkTankSeating extends BaseModel {
  eventId: string;
  thinkTankRealTable: ThinkTankRealTable;
  weight: string;
  round: number;
  dynamicContentVoting: [string];
  profile: Profile;

  constructor(
    id: string,
    eventId: string,
    thinkTankRealTable: ThinkTankRealTable,
    weight: string,
    round: number,
    dynamicContentVoting: [string],
    profile: Profile
  ) {
    super(id);

    this.eventId = eventId;
    this.thinkTankRealTable = thinkTankRealTable;
    this.weight = weight;
    this.round = round;
    this.dynamicContentVoting = dynamicContentVoting;
    this.profile = profile;
  }
}

export class ThinkTankSeatingFactory {

  fromJSON(json: ThinkTankSeatingJSON): ThinkTankSeating {

    const realTableFactory = new ThinkTankRealTableFactory();
    const profileFactory = new ProfileFactory();

    return new ThinkTankSeating(
      json.id,
      json.event_id,
      realTableFactory.fromJSON(json.think_tank_real_table),
      json.weight,
      json.round,
      json.dynamic_content_voting,
      json.profile
    );
  }

  toJSON(seatedRegistration: ThinkTankSeating): ThinkTankSeatingJSON {
    const realTableFactory = new ThinkTankRealTableFactory();
    const profileFactory = new ProfileFactory();

    return {
      id: seatedRegistration.id,
      event_id: seatedRegistration.eventId,
      think_tank_real_table: realTableFactory.toJSON(seatedRegistration.thinkTankRealTable),
      weight: seatedRegistration.weight,
      round: seatedRegistration.round,
      dynamic_content_voting: seatedRegistration.dynamicContentVoting,
      profile: profileFactory.toJSON(seatedRegistration.profile)
    };
  }
}


/*
export interface SeatedThinkTankJSON extends BaseJSON {
  id: string;
  event_id: string;
  think_tank_id: string;
  think_tank_name: string;
  think_tank_title: string;
  round: number;
  contents: ThinkTankContentJSON [];
}

export class SeatedThinkTank extends BaseModel {
  eventId: string;
  thinkTankId: string;
  thinkTankName: string;
  thinkTankTitle: string;
  round: number;
  contents: ThinkTankContent [];

  constructor(
    id: string,
    eventId: string,
    thinkTankId: string,
    thinkTankName: string,
    thinkTankTitle: string,
    round: number,
    contents: ThinkTankContent []
  ) {
    super(id);

    this.eventId = eventId;
    this.thinkTankId = thinkTankId;
    this.thinkTankName = thinkTankName;
    this.thinkTankTitle = thinkTankTitle;
    this.round = round;
    this.contents = contents;
  }
}

export class SeatedThinkTankFactory {
  fromJSON(json: SeatedThinkTankJSON): SeatedThinkTank {
    return new SeatedThinkTank(
      json.id,
      json.event_id,
      json.think_tank_id,
      json.think_tank_name,
      json.think_tank_title,
      json.round,
      json.contents.map((thinkTankContentJSON: ThinkTankContentJSON) => new ThinkTankContent(
          thinkTankContentJSON.id,
          thinkTankContentJSON.title,
          thinkTankContentJSON.language_code,
          thinkTankContentJSON.department,
          thinkTankContentJSON.topic,
          thinkTankContentJSON.sub_topic,
          thinkTankContentJSON.sub_sub_topic,
          thinkTankContentJSON.content1,
          thinkTankContentJSON.function_related_content1,
          thinkTankContentJSON.function_related_content2,
          thinkTankContentJSON.function_related_content3,
          thinkTankContentJSON.function_related_content4,
          thinkTankContentJSON.function_related_content5

      ))
    );
  }

  toJSON(seatedThinkTank: SeatedThinkTank): SeatedThinkTankJSON {
    return {
      id: seatedThinkTank.id,
      event_id: seatedThinkTank.eventId,
      think_tank_id: seatedThinkTank.thinkTankId,
      think_tank_name: seatedThinkTank.thinkTankName,
      think_tank_title: seatedThinkTank.thinkTankTitle,
      round: seatedThinkTank.round,
      contents: seatedThinkTank.contents.map((thinkTankContent: ThinkTankContent) => ({
        id: thinkTankContent.id,
        title: thinkTankContent.title,
        language_code: thinkTankContent.languageCode,
        department: thinkTankContent.department,
        topic: thinkTankContent.topic,
        sub_topic: thinkTankContent.subTopic,
        sub_sub_topic: thinkTankContent.subSubTopic,
        content1: thinkTankContent.content1,
        function_related_content1: thinkTankContent.functionRelatedContent1,
        function_related_content2: thinkTankContent.functionRelatedContent2,
        function_related_content3: thinkTankContent.functionRelatedContent3,
        function_related_content4: thinkTankContent.functionRelatedContent4,
        function_related_content5: thinkTankContent.functionRelatedContent5
      }))
    };
  }
}

*/



export interface DinnerPriorityJSON extends BaseJSON {
  id: string;
  main_registration: string;
  wish_seating_partner: string;
  priority: string;
}

export class DinnerPriority extends BaseModel {
  mainRegistration: string;
  wishSeatingPartner: string;
  priority: string;

  constructor(
    id: string,
    mainRegistration: string,
    wishSeatingPartner: string,
    priority: string
  ) {
    super(id);

    this.mainRegistration = mainRegistration;
    this.wishSeatingPartner = wishSeatingPartner;
    this.priority = priority;
  }
}

export class DinnerPriorityFactory {
  fromJSON(json: DinnerPriorityJSON): Priority {
    return new DinnerPriority(
      json.id,
      json.main_registration,
      json.wish_seating_partner,
      json.priority
    );
  }

  toJSON(priority: DinnerPriority): DinnerPriorityJSON {
    return {
      id: priority.id,
      main_registration: priority.mainRegistration,
      wish_seating_partner: priority.wishSeatingPartner,
      priority: priority.priority,
    };
  }
}

export interface Notification {
  id?: number;
  message: string;
  dismissed?: boolean;
  options: {
    variant?: "error" | "default" | "success" | "warning" | "info";
    onClose?: () => void;
  };
}

export interface DinnerSeatingJSON extends BaseJSON {
  event_id: string;
  registration_id: string;
  registration_name: string;
  registration_business_partner: string;
  type: string;
  side_of_table: number;
  seat_id: number;
  table_id: number;
  round: number;
  is_seated: boolean;
  seat_number: number;
}

export class DinnerSeating extends BaseModel {
  eventID: string;
  registrationID: string;
  registrationName: string;
  registrationBusinessPartner: string;
  round: number;
  type: string;
  sideOfTable: number;
  seatID: number;
  tableID: number;
  isSeated: boolean;
  seatNumber: number;

  constructor(
    id: string,
    eventID: string,
    registrationID: string,
    registrationName: string,
    registrationBusinessPartner: string,
    round: number,
    type: string,
    sideOfTable: number,
    seatID: number,
    tableID: number,
    isSeated: boolean,
    seatNumber: number,
  ) {
    super(id);

    this.eventID = eventID;
    this.registrationID = registrationID;
    this.registrationName = registrationName;
    this.registrationBusinessPartner = registrationBusinessPartner;
    this.round = round;
    this.type = type;
    this.sideOfTable = sideOfTable;
    this.seatID = seatID;
    this.tableID = tableID;
    this.isSeated = isSeated;
    this.seatNumber = seatNumber;
  }
}

export class DinnerSeatingFactory {
  fromJSON(json: DinnerSeatingJSON): DinnerSeating {
    return new DinnerSeating(
      json.id,
      json.event_id,
      json.registration_id,
      json.registration_name,
      json.registration_business_partner,
      json.round,
      json.type,
      json.side_of_table,
      json.seat_id,
      json.table_id,
      json.is_seated,
      json.seat_number
    );
  }

  toJSON(dinnerSeating: DinnerSeating): DinnerSeatingJSON {
    return {
      id: dinnerSeating.id,
      event_id: dinnerSeating.eventID,
      registration_id: dinnerSeating.registrationID,
      registration_name: dinnerSeating.registrationName,
      registration_business_partner: dinnerSeating.registrationBusinessPartner,
      type: dinnerSeating.type,
      side_of_table: dinnerSeating.sideOfTable,
      seat_id: dinnerSeating.seatID,
      table_id: dinnerSeating.tableID,
      round: dinnerSeating.round,
      is_seated: dinnerSeating.isSeated,
      seat_number: dinnerSeating.seatNumber,
    };
  }
}
